import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Popover } from '@abyss/web/ui/Popover';
import { Text } from '@abyss/web/ui/Text';
import { find } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLagoon } from '../../../../hooks/useLagoon';
import { ConstantsLagoon } from '../../../ConstantsLagoon';
import { h2SmallerFontForResponsive, lg, md } from '../../../ConstantsStyles';
import { LinkWithTracking } from '../../../LinkWithTracking/LinkWithTracking';
import {
  networkAffiliationsCardsContainer,
  networkAffiliationsCardsContainerCollapsed,
  networkAffiliationsHeaderContainer,
  networkAffiliationsIconStyle,
  networkAffiliationsLinkContainer,
  networkAffiliationsLinkStyle,
  networkAffiliationsSectionContainer,
  networkAffiliationsTitleContainer,
  networkAffiliationsTitleStyle,
  networkAffiliationsViewAllDetailsLink,
} from './NetworkAffiliations.style';
import {
  NetworkAffiliation,
  NetworkAffiliationsCard,
} from './NetworkAffiliationsCard';
import { NetworkAffiliationsSideDrawer } from './NetworkAffiliationsSideDrawer';

interface NetworkAffiliationsSectionProps {
  affiliations: NetworkAffiliation[];
  maxCardsToShow?: number;
  maxCardsToShowMobile?: number;
  maxCardsToShowTablet?: number;
  pcpId?: string;
  coverageType?: string;
  providerType?: string;
  isBehavioralHealth?: boolean;
  onSearchAll?: (affiliation: NetworkAffiliation) => void;
}

export const NetworkAffiliationsSection: React.FC<
  NetworkAffiliationsSectionProps
> = ({
  affiliations,
  maxCardsToShow = 3,
  maxCardsToShowMobile = 1,
  maxCardsToShowTablet = 2,
  pcpId,
  providerType,
  coverageType,
  isBehavioralHealth,
  onSearchAll,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedAffiliationId, setSelectedAffiliationId] = useState('');
  const [calculatedMaxCards, setCalculatedMaxCards] = useState(maxCardsToShow);
  const cardsContainerRef = useRef<HTMLDivElement>(null);

  const isMobile = useMediaQuery(`(max-width: ${md - 1}px)`);
  const isTablet = useMediaQuery(
    `(min-width: ${md}px) and (max-width: ${lg - 1}px)`
  );

  useEffect(() => {
    const calculateMaxCards = () => {
      if (isTablet) {
        setCalculatedMaxCards(maxCardsToShowTablet);
        return;
      }

      if (isMobile) {
        setCalculatedMaxCards(maxCardsToShowMobile);
        return;
      }

      const cardsToShow = maxCardsToShow;

      if (cardsToShow !== calculatedMaxCards) {
        setCalculatedMaxCards(cardsToShow);
        if (isExpanded) {
          setIsExpanded(false);
        }
      }
    };

    calculateMaxCards();

    const timeoutId1 = setTimeout(calculateMaxCards, 0);
    const timeoutId2 = setTimeout(calculateMaxCards, 100);
    const timeoutId3 = setTimeout(calculateMaxCards, 300);

    let resizeObserver: ResizeObserver | null = null;
    if (cardsContainerRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        calculateMaxCards();
      });
      resizeObserver.observe(cardsContainerRef.current);

      const firstCard = cardsContainerRef.current.querySelector(
        '[data-testid^="network-affiliation-card-"]'
      ) as HTMLElement;
      if (firstCard) {
        resizeObserver.observe(firstCard);
      }
    }

    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateMaxCards, 150);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
      clearTimeout(resizeTimeout);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile, isTablet, maxCardsToShow, calculatedMaxCards, isExpanded]);

  const handleOnClick = (networkAffiliationId: string) => {
    setSelectedAffiliationId(networkAffiliationId);
    setIsDrawerOpen(true);
  };

  const uiMessaging = useLagoon('ui-messaging')() || [];
  const infoMessageObj = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.NETWORK_AFFILIATIONS_INFO_ICON_MSG,
  });
  const infoMessage = infoMessageObj?.message || '';

  const totalCount = affiliations.length;

  const getMaxCards = () => {
    if (isTablet) return maxCardsToShowTablet;
    if (isMobile) return maxCardsToShowMobile;
    return calculatedMaxCards;
  };

  const maxCards = getMaxCards();
  const desktopColumns = Math.max(Math.min(maxCards, totalCount), 1);
  const cardsToDisplay = (
    isExpanded ? affiliations : affiliations.slice(0, maxCards)
  ).map((affiliation) => ({ ...affiliation }));

  const infoPopoverContent = (
    <Text
      css={{
        width: '276px',
        fontFamily: 'sans-serif',
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '140%',
        color: '#4B4D4F',
      }}
    >
      {infoMessage}
    </Text>
  );

  const shouldShowShowAllLink = totalCount > maxCards && !isExpanded;
  const shouldShowViewAllDetailsLink = totalCount > 1;

  const handleShowAllClick = () => {
    setIsExpanded(true);
  };

  if (!affiliations || affiliations.length === 0) {
    return null;
  }

  return (
    <>
      <NetworkAffiliationsSideDrawer
        affiliations={affiliations}
        coverageType={coverageType}
        isBehavioralHealth={isBehavioralHealth}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSearchAll={onSearchAll}
        pcpId={pcpId}
        providerType={providerType}
        selectedAffiliationId={selectedAffiliationId}
      />
      <Flex
        css={networkAffiliationsSectionContainer}
        data-auto-testid="network-affiliations-section"
        data-testid="network-affiliations-section"
        direction="column"
        id="network-affiliations-section"
      >
        <Flex css={networkAffiliationsHeaderContainer}>
          <Flex css={networkAffiliationsTitleContainer}>
            <Heading
              css={{
                ...h2SmallerFontForResponsive,
                ...networkAffiliationsTitleStyle,
              }}
              data-auto-testid="network-affiliations-title"
              data-testid="network-affiliations-title"
              display="h4"
              offset={1}
            >
              {t('Network affiliations')} ({totalCount})
            </Heading>
            <Popover
              align="start"
              aria-label={`${t('INFORMATION_ABOUT')} ${t(
                'Network affiliations'
              )}`}
              content={infoPopoverContent}
              css={{
                '.abyss-popover-close-button': {
                  width: '24px',
                  height: '24px',
                },
                '.abyss-popover-close-icon': {
                  color: '#4B4D4F',
                  width: '24px',
                  height: '24px',
                },
              }}
              data-auto-testid="network-affiliations-info-popover"
              data-testid="network-affiliations-info-popover"
              onOpenChange={() => setIsInfoOpen(!isInfoOpen)}
              open={isInfoOpen}
              width={300}
            >
              <IconMaterial
                css={{ cursor: 'pointer' }}
                data-auto-testid="network-affiliations-info-icon"
                data-testid="network-affiliations-info-icon"
                icon="info"
                onClick={() => setIsInfoOpen(true)}
                size={16}
                variant="outlined"
              />
            </Popover>
          </Flex>
          {shouldShowViewAllDetailsLink && (
            <LinkWithTracking
              after={
                <IconMaterial
                  css={networkAffiliationsIconStyle}
                  icon="chevron_right"
                  size="20px"
                />
              }
              analyticsInfo={{
                name: 'view all affiliation details',
                location: 'body:network-affiliations',
              }}
              css={networkAffiliationsViewAllDetailsLink}
              data-auto-testid="view-all-affiliation-details-link"
              data-testid="view-all-affiliation-details-link"
              isStandardAnchor
              onClick={() =>
                handleOnClick(affiliations[0].networkAffiliationId + 0)
              }
            >
              {t('View all affiliation details')}
            </LinkWithTracking>
          )}
        </Flex>

        <div ref={cardsContainerRef}>
          <Flex
            css={{
              ...(isExpanded && !isMobile
                ? networkAffiliationsCardsContainer
                : networkAffiliationsCardsContainerCollapsed),
              ...(!isMobile &&
                !isTablet && {
                  display: 'grid',
                  gridTemplateColumns: `repeat(${desktopColumns}, minmax(0, 344px))`,
                  alignItems: 'stretch',
                }),
            }}
            data-testid="network-affiliations-cards-container"
          >
            {cardsToDisplay.map((affiliation, index) => (
              <NetworkAffiliationsCard
                affiliation={affiliation}
                key={`${affiliation.networkAffiliationId}-${index}`}
                onClick={() =>
                  handleOnClick(affiliation.networkAffiliationId + index)
                }
                testId={`network-affiliation-card-${index}`}
              />
            ))}
          </Flex>
        </div>

        {shouldShowShowAllLink && (
          <Flex css={networkAffiliationsLinkContainer}>
            <LinkWithTracking
              after={
                <IconMaterial
                  css={networkAffiliationsIconStyle}
                  icon="expand_more"
                  size="20px"
                />
              }
              analyticsInfo={{
                name: `show all ${totalCount}`,
                location: 'body:network-affiliations',
              }}
              css={networkAffiliationsLinkStyle}
              data-auto-testid="show-all-affiliations-link"
              data-testid="show-all-affiliations-link"
              isStandardAnchor
              onClick={handleShowAllClick}
            >
              {t('Show all')} ({totalCount})
            </LinkWithTracking>
          </Flex>
        )}
      </Flex>
    </>
  );
};
